import { useMemo, useState } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Database, Download, MoreHorizontal, Plus, Search, X } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/PageHeader';
import { Skeleton } from '../../components/Skeleton';
import { Pagination, usePagination } from '../../components/Pagination';
import { DateRangeFilter, inRange, ALL_TIME, type DateRange } from '../../components/DateRangeFilter';
import { cn } from '../../lib/cn';
import { downloadCsv } from '../../lib/csv';
import { useAdminBusinesses, useResellers, usePendingExtractions } from '../../api/admin';
import { apiPost } from '../../api/client';

// Record<string, …> + fallback: covers every business_status the API can emit
// (the DB enum has 7 values; the happy-path mock only used 3) without a gray badge.
const STATUS_TONE: Record<string, 'success' | 'info' | 'warning' | 'error' | 'neutral'> = {
  active: 'success',
  trial: 'info',
  past_due: 'warning',
  suspended: 'error',
  canceled: 'neutral',
  pending_payment: 'warning',
  provisioning: 'info',
  deleted: 'neutral',
};
const STATUS_TONE_FALLBACK = 'neutral' as const;

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  trial: 'Trial',
  past_due: 'Past due',
  suspended: 'Suspended',
  canceled: 'Canceled',
  pending_payment: 'Pending pay',
  provisioning: 'Provisioning',
  deleted: 'Deleted',
};

// Must cover EVERY businesses.db_mode value. 'shared' — the DEFAULT for new businesses —
// was missing, so the most common mode rendered as a BLANK cell and could not be filtered.
const DB_LABEL: Record<string, string> = {
  shared: 'Shared',
  managed: 'Managed',
  self_hosted: 'Self-host',
  legacy: 'Legacy',
};

/**
 * Pseudo-status matching the Overview "Past due" tile, which counts
 * `businesses.status in ('past_due','suspended')` (admin.service.ts). A plain
 * single-status link from that tile would land on a list SHORTER than the number
 * the tile just showed, which reads as a bug — so the filter has to mean the same
 * thing the tile means.
 */
const NEEDS_ATTENTION = 'needs_attention';
const NEEDS_ATTENTION_STATUSES = ['past_due', 'suspended'];

/** `2026-08-01T…` → `1 Aug 2026`. Missing/unparseable → em dash, never a fabricated date. */
function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Colour the expiry so an admin scanning the list sees trouble without reading dates. */
function expiryTone(iso: string | null | undefined): string {
  if (!iso) return 'text-zinc-400 dark:text-zinc-500';
  const days = (new Date(iso).getTime() - Date.now()) / 86_400_000;
  if (Number.isNaN(days)) return 'text-zinc-400 dark:text-zinc-500';
  if (days < 0) return 'text-rose-600 dark:text-rose-400 font-medium'; // already lapsed
  if (days <= 14) return 'text-amber-700 dark:text-amber-300'; // renewing soon
  return 'text-zinc-600 dark:text-zinc-300';
}

/** All businesses across all resellers — multi-filter, bulk actions footer. */
/**
 * The extraction queue, surfaced where an admin already is.
 *
 * Approve/decline live on one business's Extraction tab, which meant a request could only be
 * found by someone who already knew which business had made one — so a reseller's request
 * could wait indefinitely with nobody aware (2026-08-09). This panel and the sidebar badge
 * are the discovery path; the tab is still where the decision happens.
 *
 * Renders nothing at all when the queue is empty — a permanently-present "0 pending" card is
 * furniture, and furniture is not read.
 */
function PendingExtractionsPanel() {
  const { data: pending = [] } = usePendingExtractions();
  if (pending.length === 0) return null;

  const awaitingApproval = pending.filter((e) => !e.approved);
  const scheduled = pending.filter((e) => e.approved);

  return (
    <Card className="mb-4 overflow-hidden">
      <div className="px-5 sm:px-6 py-3.5 border-b border-zinc-200 dark:border-zinc-700 flex items-center gap-2">
        <Database className="w-4 h-4 text-zinc-400 dark:text-zinc-500 shrink-0" />
        <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Database extraction queue</h2>
        {awaitingApproval.length > 0 && (
          <Badge tone="warning">{awaitingApproval.length} awaiting approval</Badge>
        )}
      </div>
      <ul className="divide-y divide-zinc-200 dark:divide-zinc-700">
        {[...awaitingApproval, ...scheduled].map((e) => (
          <li key={e.publicId} className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <Link
              to="/admin/businesses/$id"
              params={{ id: e.businessPublicId }}
              className="text-sm font-medium text-zinc-900 dark:text-zinc-100 hover:underline"
            >
              {e.businessName}
            </Link>
            <span className="text-xs text-zinc-500 dark:text-zinc-400 font-mono">{e.sourceSchema}</span>
            {e.approved ? (
              <Badge tone="info">Runs {e.scheduledFor ?? 'on its scheduled night'}</Badge>
            ) : (
              <Badge tone="warning">Needs a decision</Badge>
            )}
            <span className="text-xs text-zinc-500 dark:text-zinc-400 ml-auto tabular-nums">
              requested {e.requestedAt ? new Date(e.requestedAt).toLocaleDateString() : '—'}
            </span>
          </li>
        ))}
      </ul>
      {scheduled.length > 1 && (
        // Stated explicitly because a queue of approvals for one night looks like a fault
        // otherwise. The one-per-night limit was removed on 2026-08-10 (runDue ticks every
        // 10 min across 23:30 → 01:30), but this sentence was left behind saying it still
        // applied — and a client operator read it, saw several run, and reported the UI as
        // wrong. Keep it describing what runDue actually does, including the stand-down.
        <p className="px-5 sm:px-6 py-2.5 text-xs text-zinc-500 dark:text-zinc-400 border-t border-zinc-200 dark:border-zinc-700">
          Extractions run one at a time from 11:30 pm, several per night, oldest first — no
          action needed. If one fails, the rest of that night stands down and the queue
          resumes the following night.
        </p>
      )}
    </Card>
  );
}

export function Businesses() {
  const { data: adminBusinesses = [], isLoading } = useAdminBusinesses();
  const { data: resellers = [] } = useResellers();
  // Deep-linkable so the Overview tiles can point here (?status=needs_attention).
  const urlSearch = useSearch({ strict: false }) as { status?: string };
  const [resellerFilter, setResellerFilter] = useState<string>('all');
  const [planFilter, setPlanFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>(urlSearch.status ?? 'all');
  const [dbFilter, setDbFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  // #11 — filters on the business's CREATED date.
  const [dateRange, setDateRange] = useState<DateRange>(ALL_TIME);
  // The plan list is whatever the catalog actually holds right now, read off the rows.
  const planOptions = useMemo(
    () => [...new Set(adminBusinesses.map((b) => b.plan).filter(Boolean))].sort(),
    [adminBusinesses],
  );

  const filtered = useMemo(() => {
    return adminBusinesses.filter((b) => {
      if (resellerFilter !== 'all') {
        if (resellerFilter === 'direct' ? b.resellerId !== null : b.resellerId !== resellerFilter) return false;
      }
      if (planFilter !== 'all' && b.plan !== planFilter) return false;
      if (statusFilter === NEEDS_ATTENTION) {
        if (!NEEDS_ATTENTION_STATUSES.includes(b.status)) return false;
      } else if (statusFilter !== 'all' && b.status !== statusFilter) return false;
      if (dbFilter !== 'all' && b.dbMode !== dbFilter) return false;
      if (!inRange(b.createdAt, dateRange)) return false;
      if (search && !b.name.toLowerCase().includes(search.toLowerCase()) && !b.ownerEmail.includes(search.toLowerCase()) && !b.id.includes(search)) return false;
      return true;
    });
  }, [resellerFilter, planFilter, statusFilter, dbFilter, search, dateRange, adminBusinesses]);

  // Locked decision: 25 rows by default, selector 25/50/100/200. Paginates the
  // FILTERED list, so filters and CSV export still see every matching row.
  const pager = usePagination(filtered);


  function exportCsv() {
    downloadCsv(
      `businesses-${new Date().toISOString().slice(0, 10)}`,
      ['id', 'name', 'owner_email', 'reseller', 'plan', 'status', 'db_mode'],
      filtered.map((b) => [b.id, b.name, b.ownerEmail, b.resellerName ?? 'Direct', b.plan, b.status, b.dbMode]),
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-[1440px] mx-auto pb-32">
      <PageHeader
        eyebrow="Platform"
        title={<span>Businesses {isLoading ? <Skeleton className="inline-block w-12 h-5 rounded align-middle" /> : <span className="text-zinc-400 dark:text-zinc-500 font-medium tabular-nums">({adminBusinesses.length})</span>}</span>}
        subtitle="Every Possignin business across every reseller and direct sale."
        action={
          <>
            <Button variant="secondary" size="sm" iconLeft={<Download className="w-4 h-4" />} onClick={exportCsv}>
              <span className="hidden sm:inline">Export CSV</span>
            </Button>
            <Link to="/admin/businesses/new">
              <Button size="sm" iconLeft={<Plus className="w-4 h-4" />}>New business</Button>
            </Link>
          </>
        }
      />

      <PendingExtractionsPanel />

      <Card className="overflow-hidden">
        <div className="px-5 sm:px-6 py-3 flex flex-wrap items-center gap-2 border-b border-zinc-200 dark:border-zinc-700">
          <FilterChip
            label="Reseller"
            value={
              resellerFilter === 'all'
                ? 'All'
                : resellerFilter === 'direct'
                ? 'Direct'
                : resellers.find((r) => r.id === resellerFilter)?.name ?? 'All'
            }
            options={[
              { v: 'all', l: 'All' },
              { v: 'direct', l: 'Direct (no reseller)' },
              ...resellers.map((r) => ({ v: r.id, l: r.name })),
            ]}
            onChange={setResellerFilter}
          />
          <FilterChip
            label="Plan"
            value={planFilter === 'all' ? 'All' : planFilter}
            /* Derived from the rows on screen, not a hardcoded list. The old one named a
               RETIRED catalog (Pro/Enterprise): those two always matched nothing, while
               the current top-tier packages could not be filtered at all. */
            options={[{ v: 'all', l: 'All' }, ...planOptions.map((p) => ({ v: p, l: p }))]}
            onChange={setPlanFilter}
          />
          <FilterChip
            label="Status"
            value={
              statusFilter === 'all'
                ? 'All'
                : statusFilter === NEEDS_ATTENTION
                  ? 'Past due / Suspended'
                  : STATUS_LABEL[statusFilter] ?? statusFilter
            }
            /* Exactly the businesses_status_ck values. 'trial' was offered but is a
               SUBSCRIPTION status, never a business one, so it always returned nothing;
               meanwhile pending_payment / provisioning / deleted — which businesses really
               do hold — could not be filtered at all. */
            options={[
              { v: 'all', l: 'All' },
              { v: NEEDS_ATTENTION, l: 'Past due / Suspended' },
              { v: 'pending_payment', l: 'Pending payment' },
              { v: 'provisioning', l: 'Provisioning' },
              { v: 'active', l: 'Active' },
              { v: 'past_due', l: 'Past due' },
              { v: 'suspended', l: 'Suspended' },
              { v: 'canceled', l: 'Canceled' },
              { v: 'deleted', l: 'Deleted' },
            ]}
            onChange={setStatusFilter}
          />
          <FilterChip
            label="DB"
            value={dbFilter === 'all' ? 'All' : DB_LABEL[dbFilter] ?? dbFilter}
            options={[
              { v: 'all', l: 'All' },
              { v: 'shared', l: 'Shared' },
              { v: 'managed', l: 'Managed' },
              { v: 'self_hosted', l: 'Self-hosted' },
              { v: 'legacy', l: 'Legacy' },
            ]}
            onChange={setDbFilter}
          />
          <DateRangeFilter label="Created" value={dateRange} onChange={setDateRange} />
          <div className="ml-auto w-full sm:w-72">
            <Input
              placeholder="Search by name, owner, domain, business_id…"
              prefix={<Search className="w-4 h-4" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto nice-scroll">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 bg-zinc-50/50 dark:bg-zinc-900/50">
              <tr>
                <th className="text-left font-medium px-5 sm:px-6 py-3">Name</th>
                <th className="text-left font-medium px-4 py-3">Reseller</th>
                <th className="text-left font-medium px-4 py-3">Plan</th>
                <th className="text-left font-medium px-4 py-3">Status</th>
                <th className="text-left font-medium px-4 py-3 hidden md:table-cell">DB</th>
                <th className="text-left font-medium px-4 py-3 hidden lg:table-cell">Created</th>
                <th className="text-left font-medium px-4 py-3 hidden lg:table-cell">Expires</th>
                <th className="px-5 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200 dark:divide-zinc-700">
              {isLoading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    <td className="px-5 sm:px-6 py-3">
                      <Skeleton className="h-4 w-40 max-w-full rounded mb-1.5" />
                      <Skeleton className="h-3 w-28 max-w-full rounded" />
                    </td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-20 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-16 rounded" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
                    <td className="px-4 py-3 hidden md:table-cell"><Skeleton className="h-4 w-14 rounded" /></td>
                    <td className="px-4 py-3 hidden lg:table-cell"><Skeleton className="h-4 w-20 rounded" /></td>
                    <td className="px-4 py-3 hidden lg:table-cell"><Skeleton className="h-4 w-20 rounded" /></td>
                    <td className="px-5 sm:px-6 py-3"></td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                    No businesses match these filters.
                  </td>
                </tr>
              ) : (
                pager.pageItems.map((b) => (
                <tr key={b.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/70 transition">
                  <td className="px-5 sm:px-6 py-3">
                    <Link
                      to="/admin/businesses/$id"
                      params={{ id: b.id }}
                      className="block min-w-0 group"
                    >
                      <div className="font-medium text-zinc-950 dark:text-zinc-50 group-hover:text-brand dark:group-hover:text-brand-dm truncate transition">
                        {b.name}
                      </div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{b.ownerEmail}</div>
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200 whitespace-nowrap">
                    {b.resellerName ?? <span className="text-zinc-400 dark:text-zinc-500">— Direct</span>}
                  </td>
                  <td className="px-4 py-3 text-zinc-700 dark:text-zinc-200">{b.plan}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Badge tone={STATUS_TONE[b.status] ?? STATUS_TONE_FALLBACK}>{STATUS_LABEL[b.status] ?? b.status}</Badge>
                      {b.paymentReviewAt && <ReviewFlag id={b.id} />}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 hidden md:table-cell">{DB_LABEL[b.dbMode]}</td>
                  <td className="px-4 py-3 text-zinc-600 dark:text-zinc-300 whitespace-nowrap tabular-nums hidden lg:table-cell">{shortDate(b.createdAt)}</td>
                  <td className="px-4 py-3 whitespace-nowrap tabular-nums hidden lg:table-cell">
                    <span className={expiryTone(b.expiresAt)}>{shortDate(b.expiresAt)}</span>
                  </td>
                  <td className="px-5 sm:px-6 py-3 text-right">
                    <ProvisionAction id={b.id} dbMode={b.dbMode} posLinked={b.posLinked} />
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination state={pager} label="businesses" />

        <div className="px-5 sm:px-6 py-3 border-t border-zinc-200 dark:border-zinc-700 text-xs text-zinc-500 dark:text-zinc-400 flex items-center justify-between">
          {isLoading ? <Skeleton className="h-3 w-28 rounded" /> : <span>Matching <span className="tabular-nums">{filtered.length}</span> of <span className="tabular-nums">{adminBusinesses.length}</span></span>}
          <span className="hidden sm:inline">Click any row to drill in.</span>
        </div>
      </Card>

    </div>
  );
}

/** The payment-review badge + a clear affordance (admin resolved the chargeback). */
function ReviewFlag({ id }: { id: string }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  async function clear() {
    setBusy(true);
    try {
      await apiPost(`/admin/businesses/${id}/clear-review`);
      void qc.invalidateQueries({ queryKey: ['admin', 'businesses'] });
    } catch {
      /* leave the flag set on error */
    } finally {
      setBusy(false);
    }
  }
  return (
    <span className="inline-flex items-center gap-0.5">
      <Badge tone="warning">Review</Badge>
      <button
        type="button"
        onClick={clear}
        disabled={busy}
        title="Clear payment review (resolved)"
        className="w-4 h-4 grid place-items-center rounded text-amber-600 hover:bg-amber-100 dark:text-amber-400 dark:hover:bg-amber-900/40 transition disabled:opacity-50"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

/** Row action: trigger dedicated-DB provisioning for a managed business (idempotent). */
function ProvisionAction({ id, dbMode, posLinked }: { id: string; dbMode: string; posLinked?: boolean }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  // A NON-managed business whose keystone bootstrap failed has no POS behind it and used
  // to have no retry anywhere: /provision is managed-only and the drain only scans
  // business_databases, which shared businesses have no row in. Offer the all-modes
  // keystone retry for exactly those rows.
  const needsPosBootstrap = dbMode !== 'managed' && posLinked === false;

  if (dbMode !== 'managed' && !needsPosBootstrap) {
    return (
      <button type="button" disabled className="w-8 h-8 grid place-items-center rounded-md text-zinc-300 dark:text-zinc-600 cursor-default">
        <MoreHorizontal className="w-4 h-4" />
      </button>
    );
  }

  async function provision() {
    setBusy(true);
    setResult(null);
    try {
      const path = needsPosBootstrap ? 'bootstrap-pos' : 'provision';
      const r = await apiPost<{ status: string }>(`/admin/businesses/${id}/${path}`);
      setResult(r.status);
      void qc.invalidateQueries({ queryKey: ['admin', 'businesses'] });
    } catch {
      setResult('error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={provision}
      disabled={busy}
      title={needsPosBootstrap ? 'This business has no POS behind it — retry the POS bootstrap' : 'Provision dedicated database'}
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition',
        result === 'ready'
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800'
          : result === 'error'
            ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/60 dark:text-red-300 dark:border-red-800'
            : 'bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800',
      )}
    >
      <Database className="w-3.5 h-3.5" />
      {busy
        ? (needsPosBootstrap ? 'Bootstrapping…' : 'Provisioning…')
        : result === 'ready' ? 'Ready'
        : result === 'error' ? 'Failed'
        : result ?? (needsPosBootstrap ? 'Fix POS link' : 'Provision')}
    </button>
  );
}

function FilterChip({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ v: string; l: string }>;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 border border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition"
      >
        <span className="text-zinc-500 dark:text-zinc-400">{label}:</span>
        <span>{value}</span>
        <ChevronDown className="w-3 h-3 text-zinc-400" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute top-full mt-1 left-0 z-20 min-w-[180px] rounded-md border border-zinc-200 dark:border-zinc-600 bg-white dark:bg-zinc-900 shadow-md py-1 max-h-64 overflow-y-auto">
            {options.map((o) => (
              <button
                key={o.v}
                type="button"
                onClick={() => {
                  onChange(o.v);
                  setOpen(false);
                }}
                className={cn(
                  'block w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-50 dark:hover:bg-zinc-800',
                  value === o.l ? 'text-brand dark:text-brand-dm font-medium' : 'text-zinc-700 dark:text-zinc-200',
                )}
              >
                {o.l}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
